import React, { useEffect, useState } from "react";

// region External Libraries
import _ from "lodash";
import { matchesSubstringIgnoreCaseAndAccents } from "@npm-telluria-tecnologia/telluria-utils";
import { useLazyQuery } from "@apollo/client";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
import { useAtom } from "jotai";
import { useMsal } from "@azure/msal-react";
// endregion External Libraries
// region Languages
import { FilterMessages, GlobalMessages, RoleRegisterMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
// endregion Languages
// region Helpers
import { sortAlphabetically } from "src/helpers/utils";
// endregion Helpers
// region Atoms
import Loading from "@atoms/Loading";
import UserSelectionCard from "@atoms/UserSelectionCard";
import SearchField from "@atoms/SearchField";
import { selectedModuleIdAtom } from "src/atoms/SelectIdentityModule";
// endregion Atoms
// region Molecules
// endregion Molecules
// region Organisms
// endregion Organisms
// region Pages
// endregion Pages
// region Store
import { ECommandResultStatus, RoleType, UserQueriesGetAllWithPaginationArgs, UserRoleType, UserType } from "src/store/graphql/helpers/types.graphql";
import { TUserAllWithPaginationQueries, userAllWithPagination } from "src/store/graphql/groups/user/query.graphql";
// endregion Store
// region Styles
import * as Styled from "./styles";
// endregion Styles

interface IUserSelectionListProps {
  currentRole: RoleType;
  onUsersChange: (userIds: UserRoleType[]) => void;
}

const UserSelectionList: React.FC<IUserSelectionListProps> = ({ currentRole, onUsersChange }) => {
  // region Queries
  const [QueryGetUserAllWithPagination] = useLazyQuery<TUserAllWithPaginationQueries,
    UserQueriesGetAllWithPaginationArgs>(
      userAllWithPagination([
        "id", "name", "email", "position", "imageUrl", "description", "admin", "userRoles.id",
        "userRoles.role.module.id", "userRoles.role.module.name"
      ])
    );
  // endregion Queries
  // region Hooks
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { instance } = useMsal();
  const [selectedModuleId] = useAtom(selectedModuleIdAtom);
  // endregion Hooks

  // region States
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [users, setUsers] = useState<UserType[]>([]);
  const [displayedUsers, setDisplayedUsers] = useState<UserType[]>([]);
  // endregion States

  // region Functions
  const isUserMemberOfCurrentRole = (user: UserType) => currentRole.userRoles
    ?.some((u) => u?.userId === user.id) ?? false;

  const sortUsersByMembersOfCurrentRole = (users: UserType[]) => users.sort((a, b) => {
    const aIsMember = isUserMemberOfCurrentRole(a);
    const bIsMember = isUserMemberOfCurrentRole(b);

    if (aIsMember && bIsMember) return 0;
    if (aIsMember) return -1;
    if (bIsMember) return 1;

    return 0;
  });

  const changeUsers = (user: UserType, checked: boolean) => {
    const cloneCurrentRole = _.cloneDeep(currentRole);

    if (checked) {
      const userRoleOfSelectedModule = user.userRoles
        ?.find((userRole) => userRole?.role?.module?.id === selectedModuleId);

      if (userRoleOfSelectedModule) {
        addToast({ type: "info", title: t(RoleRegisterMessages.userIsAMemberOfAnotherRole) });
      }

      cloneCurrentRole.userRoles?.push({ userId: user.id } as UserRoleType);
    } else {
      cloneCurrentRole.userRoles = cloneCurrentRole.userRoles?.filter((userRole) => userRole?.userId !== user.id);
    }

    onUsersChange((cloneCurrentRole.userRoles ?? []) as UserRoleType[]);
  };

  const filterDataOnScreen = (termForFilteringDataOnScreen: string) => {
    if (!termForFilteringDataOnScreen) {
      setDisplayedUsers(sortUsersByMembersOfCurrentRole(users));

      return;
    }

    const match = (propValue: string) => {
      const formattedPropValue = propValue ?? "";

      return matchesSubstringIgnoreCaseAndAccents(formattedPropValue.toString(), termForFilteringDataOnScreen);
    };

    const cloneUsers = _.cloneDeep(users);

    const filteredUsers = cloneUsers.filter((user) => (
      match(user.name) || match(user.position)
    ));

    setDisplayedUsers(sortUsersByMembersOfCurrentRole(filteredUsers));
  };

  const getUsers = async () => {
    try {
      setIsLoading(true);

      const query = await QueryGetUserAllWithPagination();

      const response = query.data?.user.getAllWithPagination;

      if (response?.status !== ECommandResultStatus.SUCCESS) return [] as UserType[];

      return response.result as UserType[];
    } catch (error) {
      addToast({ type: "error", title: "Internal client error", description: error.message });

      return [] as UserType[];
    } finally {
      setIsLoading(false);
    }
  };
  // endregion Functions

  useEffect(() => {
    (async () => {
      const users = await getUsers();

      if (users.length === 0) return;

      const isCurrentUserAdmin = instance.getActiveAccount()?.idTokenClaims?.extension_Admin;

      const filteredUsers = isCurrentUserAdmin ? users : users.filter((user) => !user.admin);

      const sortedUsersAlphabetically = sortAlphabetically(filteredUsers, "name");
      const sortedUsersByMembersOfCurrentRole = sortUsersByMembersOfCurrentRole(sortedUsersAlphabetically);

      setUsers(sortedUsersByMembersOfCurrentRole);
      setDisplayedUsers(sortedUsersByMembersOfCurrentRole);
    })();

    return () => {
      setUsers([]);
      setDisplayedUsers([]);
    };
  }, []);

  return (
    <Styled.Container>
      <SearchField
        fieldLabel={t(FilterMessages.labelSearchByNameOrPosition)}
        onSearchClick={filterDataOnScreen}
        onClearClick={() => filterDataOnScreen("")}
      />
      <Styled.UsersBoard>
        <Loading loading={isLoading} />
        {displayedUsers.length === 0 && !isLoading ? (
          <Styled.NoRecordsFound>
            <span>{users.length === 0
              ? t(RoleRegisterMessages.noUsersRegistered)
              : t(GlobalMessages.noRecordsFoundText)}
            </span>
          </Styled.NoRecordsFound>
        ) : (
          displayedUsers.map((user) => (
            <UserSelectionCard
              key={user.id}
              currentUser={user}
              initialValue={isUserMemberOfCurrentRole(user)}
              onChangeCheckbox={(checked) => changeUsers(user, checked)}
            />
          ))
        )}
      </Styled.UsersBoard>
    </Styled.Container>
  );
};

export default UserSelectionList;